import { CollectionsHelpers } from "helpers/collections";
import { Subscribable } from "knockout";
import WCCSliderItem from "../item/base/base.model";
import WCCSliderPage from "../models/page";
import WCCSliderBaseAnimator, { WCCSliderBaseAnimatorConfig } from "./base";

interface WCCSliderFadeAnimatorContext<T> {
    page: WCCSliderPage<T> | undefined
    prevPage: WCCSliderPage<T> | undefined

    items: WCCSliderItem<T>[]
    prevItems: WCCSliderItem<T>[]

    offset: number
    nextShift: number
}

export interface WCCSliderFadeAnimatorConfig { }
export interface WCCSliderFadeAnimatorConfigFull<T> extends WCCSliderBaseAnimatorConfig<T>, WCCSliderFadeAnimatorConfig { }

export default class WCCSliderFadeAnimator<T> extends WCCSliderBaseAnimator<T, WCCSliderFadeAnimatorContext<T>> {
    private prevPage = ko.observable<WCCSliderPage<T>>();

    private activePage: Subscribable<WCCSliderPage<T> | undefined>

    constructor(config: WCCSliderFadeAnimatorConfigFull<T>) {
        super(config);

        this.activePage = ko.pureComputed<WCCSliderPage<T> | undefined>(() => this.pages()[this.pageIdx()]);

        this.effects.register([
            this.activePage.subscribe(_.noop), //seems like 'beforeChange' doesn't wake up pure computed so need a regular subscription
            this.activePage.subscribe(page => this.prevPage(page), null, 'beforeChange')
        ]);
    }

    protected override checkIfAllowedToAnimate() {
        const page = this.activePage();
        const prevPage = this.prevPage();

        if (page == undefined || prevPage == undefined || page === prevPage)
            return false;

        const items = page.items();
        const prevItems = prevPage.items();

        const sameItems = CollectionsHelpers.compareCollections(items, prevItems);

        if (sameItems)
            return false;

        return super.checkIfAllowedToAnimate();
    }

    protected getAnimationDuration() {
        return 500;
    }

    protected getAnimationContext() {
        const prevShift = this.shift();
        const nextShift = this.nextShift();
        const offset = nextShift - prevShift;

        const page = this.activePage();
        const items = page?.items() ?? [];

        const prevPage = this.prevPage();
        const prevItems = prevPage?.items() ?? [];

        return {
            page: this.activePage(),
            prevPage: this.prevPage(),

            items: items,
            prevItems: prevItems,

            offset: offset,
            nextShift: nextShift
        }
    }

    protected override beforeAnimation(context: WCCSliderFadeAnimatorContext<T>) {
        super.beforeAnimation(context);

        const {
            items,
            prevItems,
            offset,
            nextShift
        } = context;

        items.forEach(item => item.opacity(0));
        prevItems.forEach(item => item.offset(offset));

        this.shift(nextShift);
    }

    protected override onProgress(progress: number, context: WCCSliderFadeAnimatorContext<T>) {
        super.onProgress(progress, context);

        const { items, prevItems } = context;

        const newItemsOpacity = progress / 100;
        const oldItemsOpacity = 1 - progress / 100;

        items.forEach(item => item.opacity(newItemsOpacity));
        prevItems.forEach(item => item.opacity(oldItemsOpacity));
    }

    protected override afterAnimation(context: WCCSliderFadeAnimatorContext<T>) {
        super.afterAnimation(context);

        const { page, prevItems } = context;

        prevItems.forEach(item => {
            item.opacity(1);
            item.offset(0);
        });

        this.prevPage(page);
    }
}